import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ZonedDateTime } from '@internationalized/date';
import { cn } from '@antimatterhq/ui/lib/utils';
import { formatCompactNumber } from '@antimatterhq/ui/utils/format';
import { Provider, useSetAtom } from 'jotai';
import { HistogramTimeline } from './timeline/histogramTimeline';
import { HistogramBars, type HistogramDataPoint } from './bars/histogramBars';
import { HistogramSkeletonBars } from './bars/histogramSkeletonBars';
import { HistogramTooltip } from './tooltip/histogramTooltip';
import { getHistogramBarIntervalForRange } from './histogram.utils';
import {
  histogramTimelineDragOffsetAtom,
  histogramVisibleTimeRangeAtom,
} from './jotai/histogramAtoms';
import { HistogramLocationIndicator } from './locationIndicator/histogramLocationIndicator';

/**
 * Rounds a value to a nice round number for Y-axis display
 * Examples: 212 → 200, 275 → 300, 8 → 10, 899 → 900, 180 → 100
 */
function roundMaxValue(value: number): number {
  if (value <= 0) return 0;

  // Get the magnitude (power of 10)
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));

  // Normalize the value to 1-10 range
  const normalized = value / magnitude;

  // Round to nice number - sometimes down, sometimes up based on proximity
  let rounded;
  if (normalized <= 1.2) rounded = 1;
  else if (normalized <= 1.8)
    rounded = 2; // 180 → rounds down to 100
  else if (normalized <= 3.5) rounded = 2;
  else if (normalized <= 7) rounded = 5;
  else rounded = 10;

  return rounded * magnitude;
}

type HistogramProps = {
  data?: HistogramDataPoint[];
  initialStartTime: ZonedDateTime;
  initialEndTime: ZonedDateTime;
  timezone: string;
  onTimeRangeSelection?: (
    startTime: ZonedDateTime,
    endTime: ZonedDateTime,
    interval: number,
  ) => void;
  onTimeRangeChange?: (
    startTime: ZonedDateTime,
    endTime: ZonedDateTime,
    suggestedIntervalMs: number,
  ) => void;
  maxBarHeight?: number;
  barColor?: string;
  className?: string;
  loadedTimeRanges?: [ZonedDateTime, ZonedDateTime][];
  currentLocation?: ZonedDateTime;
  isLoading?: boolean;
  onLoadData?: (startTime: ZonedDateTime, endTime: ZonedDateTime) => void;
  onDatetimeSelect?: (datetime: ZonedDateTime) => void;
};

const HistogramContent = ({
  data,
  initialStartTime,
  initialEndTime,
  timezone,
  onTimeRangeChange,
  maxBarHeight = 40,
  barColor = '#60a5fa',
  className,
  loadedTimeRanges,
  currentLocation,
  isLoading,
  onLoadData,
  onDatetimeSelect,
  onTimeRangeSelection,
}: HistogramProps) => {
  const setVisibleTimeRange = useSetAtom(histogramVisibleTimeRangeAtom);
  const setTimelineDragOffset = useSetAtom(histogramTimelineDragOffsetAtom);

  const [tooltipData, setTooltipData] = useState<{
    value: number;
    x: number;
    y: number;
  } | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [dataPointInterval, setDataPointInterval] = useState<number>(0);

  // Sync internal state with props and notify parent on mount
  useEffect(() => {
    const interval = getHistogramBarIntervalForRange(
      initialStartTime,
      initialEndTime,
    );
    setDataPointInterval(interval);
    setVisibleTimeRange([initialStartTime, initialEndTime]);
    setTimelineDragOffset(0);
  }, [
    initialStartTime,
    initialEndTime,
    setVisibleTimeRange,
    setTimelineDragOffset,
  ]);

  useEffect(() => {
    if (isLoading) {
      setTooltipData(null);
    }
  }, [isLoading]);

  // Calculate max value and round it for Y-axis display
  const rawMaxValue = data ? Math.max(...data.map((d) => d.value), 1) : 1;
  const roundedMaxValue = roundMaxValue(rawMaxValue);

  // Calculate maxLine position and bar scaling value
  const maxLineHeight =
    roundedMaxValue >= rawMaxValue
      ? 0 // At top if rounded >= raw
      : (1 - roundedMaxValue / rawMaxValue) * maxBarHeight;

  // Use rounded value for scaling when it's higher (bars won't fill canvas, maxLine at top)
  // Use raw value for scaling when rounded is lower (bars fill canvas, maxLine partway down)
  const barScalingMaxValue =
    roundedMaxValue >= rawMaxValue ? roundedMaxValue : rawMaxValue;

  const handleDragStart = useCallback(() => {
    setTimelineDragOffset(0); // Ensure we start with 0 offset
  }, [setTimelineDragOffset]);

  const handleDragMove = useCallback(
    (deltaX: number) => {
      // Calculate pixels per millisecond for constraints
      const timelineElement = timelineRef.current?.querySelector('svg');
      if (!timelineElement) return;

      setTimelineDragOffset(deltaX);
    },
    [setTimelineDragOffset],
  );

  const handleDragEnd = useCallback(() => {
    setTimelineDragOffset((timelineDragOffset) => {
      // Get the current drag offset
      const currentOffset = timelineDragOffset;

      // Calculate final time range based on current offset
      const timelineElement = timelineRef.current?.querySelector('svg');
      if (!timelineElement || currentOffset === 0) {
        return 0;
      }

      const rect = timelineElement.getBoundingClientRect();

      setVisibleTimeRange(([currentStart, currentEnd]) => {
        const durationMs =
          currentEnd.toDate().getTime() - currentStart.toDate().getTime();
        const pixelsPerMs = rect.width / durationMs;

        const offsetMs = -currentOffset / pixelsPerMs;
        const newStartTime = currentStart.add({ milliseconds: offsetMs });
        const newEndTime = currentEnd.add({ milliseconds: offsetMs });

        // Calculate suggested interval and notify parent
        onTimeRangeChange?.(newStartTime, newEndTime, dataPointInterval);

        return [newStartTime, newEndTime];
      });

      return 0;
    });
  }, [
    onTimeRangeChange,
    dataPointInterval,
    setVisibleTimeRange,
    setTimelineDragOffset,
  ]);

  const handleBarHover = useCallback(
    (bar: HistogramDataPoint | null, x: number, y: number) => {
      if (bar) {
        setTooltipData({
          value: bar.value,
          x,
          y,
        });
      } else {
        setTooltipData(null);
      }
    },
    [],
  );

  const handleSelectionEnd = useCallback(
    (selectionStart: ZonedDateTime, selectionEnd: ZonedDateTime) => {
      // Update internal state
      setVisibleTimeRange([selectionStart, selectionEnd]);
      setTimelineDragOffset(0);

      const interval = getHistogramBarIntervalForRange(
        selectionStart,
        selectionEnd,
      );

      setDataPointInterval(interval);

      // Calculate suggested interval and notify parent
      onTimeRangeSelection?.(selectionStart, selectionEnd, interval);
    },
    [onTimeRangeSelection, setTimelineDragOffset, setVisibleTimeRange],
  );

  const formattedMaxValue = useMemo(() => {
    return formatCompactNumber(roundedMaxValue);
  }, [roundedMaxValue]);

  return (
    <div className={cn('w-full', className)}>
      <div className="mt-4 flex">
        {/* Y-axis */}
        <div
          className="relative mr-3"
          style={{ height: maxBarHeight, width: '2rem' }}
        >
          {!isLoading && data && (
            <>
              {/* Rounded max value - positioned at maxLine height */}
              <div
                className="absolute right-0 flex items-center justify-end"
                style={{ top: `${maxLineHeight - 6}px` }}
              >
                <span className="text-xs leading-none text-gray-500">
                  {formattedMaxValue}
                </span>
              </div>

              {/* 0 value - positioned at bottom, centered with bars/timeline boundary */}
              <div
                className="absolute right-0 flex items-center justify-end"
                style={{ bottom: '-6px' }}
              >
                <span className="text-xs leading-none text-gray-500">0</span>
              </div>
            </>
          )}
        </div>

        {/* Chart area */}
        <div ref={chartAreaRef} className="relative flex-1">
          {/* Background container for maxLine - renders above grey background but below bars */}
          {!isLoading && (
            <div className="pointer-events-none absolute inset-0 z-histogramMaxLine">
              {/* maxLine - horizontal line behind bars */}
              <div
                className="absolute left-0 right-0 border-t border-gray-300 dark:border-gray-600"
                style={{ top: `${maxLineHeight}px` }}
              />
            </div>
          )}

          {/* Vertical line - renders above maxLine but below bars */}
          <HistogramLocationIndicator
            currentLocation={currentLocation}
            chartAreaRef={chartAreaRef}
            isLoading={isLoading}
          />

          {/* Histogram bars or skeleton loading state */}
          {isLoading && (
            <HistogramSkeletonBars
              maxBarHeight={maxBarHeight}
              className="mb-2"
            />
          )}
          {!isLoading && data && (
            <HistogramBars
              data={data}
              maxBarHeight={maxBarHeight}
              barColor={barColor}
              maxValue={barScalingMaxValue}
              loadedTimeRanges={loadedTimeRanges}
              onBarHover={handleBarHover}
              onSelectionEnd={handleSelectionEnd}
              onLoadData={onLoadData}
              onDatetimeSelect={onDatetimeSelect}
              className="mb-2"
            />
          )}
        </div>
      </div>

      {/* Timeline - same width as chart area */}
      <div className="flex">
        {/* Spacer to match Y-axis width */}
        <div className="mr-3" style={{ width: '2rem' }}></div>

        {/* Timeline */}
        <div className="flex-1" ref={timelineRef}>
          <HistogramTimeline
            timezone={timezone}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          />
        </div>
      </div>

      {/* Tooltip */}
      <HistogramTooltip
        value={tooltipData?.value ?? 0}
        x={tooltipData?.x ?? 0}
        y={tooltipData?.y ?? 0}
        visible={tooltipData !== null}
      />
    </div>
  );
};

const Histogram = (props: HistogramProps) => {
  return (
    <Provider>
      <HistogramContent {...props} />
    </Provider>
  );
};

export { Histogram, type HistogramDataPoint };
